import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ChevronDown, ChevronUp, Loader2, RefreshCw } from 'lucide-react';

import { getAllSources, getSource } from '@/sources/registry';
import type { ComicCard, Page, SourceId } from '@/sources/types';
import { useFilteredComics } from '@/features/blacklist';
import { ComicCard as ComicCardComp } from '@/components/ComicCard';
import { usePaginatedFetcher } from '@/components/usePaginatedFetcher';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import { type FavoriteEntry, type SourceFavoritesError, useLibrary } from './useLibrary';
import { useDedup } from './useDedup';
import { MatchGroup } from './MatchGroup';
import { sourceBadge } from './sourceBadge';

/**
 * Page size for the library grid's "Load more" affordance. The library is
 * a unified in-memory view of favorites already mirrored to IndexedDB, so
 * the gate here is purely DOM weight — large libraries with thousands of
 * favorites benefit from not rendering every card up front.
 */
const LIBRARY_PAGE_SIZE = 50;

type SortKey = 'recent' | 'title';
type SourceFilter = SourceId | 'all';
type SaveFilter = 'all' | 'app' | 'source';

export interface LibraryProps {
  className?: string;
  title?: string;
  description?: string;
}

/**
 * Unified cross-source library page.
 *
 * Composition:
 *   1. Toolbar — source filter chips + sort dropdown + refresh button + last-sync label.
 *   2. Per-source error banners (auth-required sources only).
 *   3. Grid — favorites filtered by the source chip + blacklist.
 *   4. Cross-source matches — collapsible list of dedup hint groups.
 *
 * Adding a source = it shows up automatically because the chips and the
 * dedup pass are data-driven from {@link getAllSources}.
 */
export function Library({
  className,
  title = 'Library',
  description = 'Your Bunko saves and source-site favorites across every source. Bunko saves work without source login; connected sources refresh source favorites in the background.',
}: LibraryProps) {
  const { entries, errors, staleSources, loading, refresh, lastSyncedAt } = useLibrary();
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [saveFilter, setSaveFilter] = useState<SaveFilter>('all');
  const [sort, setSort] = useState<SortKey>('recent');
  const [matchesOpen, setMatchesOpen] = useState(false);

  const sources = useMemo(
    () => getAllSources().filter((s) => s.capabilities.favorites),
    [],
  );
  const counts = useMemo(() => {
    const out = new Map<SourceId, number>();
    for (const e of entries) out.set(e.sourceId, (out.get(e.sourceId) ?? 0) + 1);
    return out;
  }, [entries]);
  const staleSourceSet = useMemo(() => new Set(staleSources), [staleSources]);

  const filteredEntries = useMemo(() => {
    let pool = entries;
    if (sourceFilter !== 'all') {
      pool = pool.filter((e) => e.sourceId === sourceFilter);
    }
    if (saveFilter === 'app') {
      pool = pool.filter((e) => e.appSaved);
    } else if (saveFilter === 'source') {
      pool = pool.filter((e) => e.sourceSaved);
    }
    const sortedPool = [...pool];
    if (sort === 'recent') sortedPool.sort((a, b) => b.addedAt - a.addedAt);
    else if (sort === 'title') {
      sortedPool.sort((a, b) => a.title.localeCompare(b.title));
    }
    return sortedPool;
  }, [entries, saveFilter, sourceFilter, sort]);

  // Convert to ComicCard shape so the existing blacklist hook / card
  // component works without a new adapter. We can't recover full tag
  // data here (favorites only snapshot title + cover), so blacklist
  // filtering matches only on the per-row `extra.tags` we stashed at
  // add-time (currently none of the sources do, so filtering is a
  // best-effort pass — see README).
  const cards = useMemo<ComicCard[]>(
    () =>
      filteredEntries.map((e) => ({
        id: e.comicId,
        source: e.sourceId,
        title: e.title,
        coverUrl: e.cover,
        tags: (e.tags ?? []).map((t) => ({ id: t, name: t })),
      })),
    [filteredEntries],
  );

  // We use the "all sources" string as the sourceId hint; the hook only
  // uses it to look up per-source rules, and the blacklist already merges
  // global + per-source rules per comic.
  const { visible } = useFilteredComics(cards, sourceFilter === 'all' ? '*' : sourceFilter);
  const entryByCardKey = useMemo(() => {
    const map = new Map<string, FavoriteEntry>();
    for (const entry of filteredEntries) {
      map.set(`${entry.sourceId}|${entry.comicId}`, entry);
    }
    return map;
  }, [filteredEntries]);

  // Paginate the rendered grid so a library with thousands of favorites
  // doesn't try to mount the full list on first paint. The fetcher slices
  // the already-loaded `visible` array — no I/O — so each "Load more" is
  // effectively instant after the initial DB read.
  const visibleSlicer = useCallback(
    async (page: number): Promise<Page<ComicCard>> => {
      const start = (page - 1) * LIBRARY_PAGE_SIZE;
      const end = start + LIBRARY_PAGE_SIZE;
      const items = visible.slice(start, end);
      return {
        items,
        page,
        hasMore: end < visible.length,
        totalItems: visible.length,
      };
    },
    [visible],
  );
  // Reset the grid pagination whenever the filtered set changes shape
  // (source chip, sort key, or refresh) — keying off length+filter keeps
  // the cost cheap while still catching the common reasons for a re-slice.
  const gridResetKey = `${sourceFilter}|${saveFilter}|${sort}|${visible.length}`;
  const {
    items: pagedVisible,
    hasMore: hasMoreToShow,
    loading: pageLoading,
    loadMore,
    loadedPages,
  } = usePaginatedFetcher<ComicCard>({
    fetcher: visibleSlicer,
    resetKey: gridResetKey,
  });

  const visibleBySource = useMemo(() => {
    const grouped = new Map<SourceId, ComicCard[]>();
    for (const card of visible) {
      const sourceId = card.source as SourceId;
      const group = grouped.get(sourceId);
      if (group) group.push(card);
      else grouped.set(sourceId, [card]);
    }
    return sources
      .map((source) => ({ source, cards: grouped.get(source.id) ?? [] }))
      .filter((group) => group.cards.length > 0);
  }, [sources, visible]);

  const dedupGroups = useDedup(entries);

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <div className="flex items-center gap-2">
          {lastSyncedAt !== undefined && (
            <span className="text-xs text-muted-foreground">
              Synced {formatRelative(lastSyncedAt)}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            disabled={loading}
            className="gap-1.5"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </header>

      {errors.map((err) => (
        <ReconnectBanner key={err.sourceId} err={err} />
      ))}

      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <Tabs
          value={sourceFilter}
          onValueChange={(v) => setSourceFilter(v as SourceFilter)}
          className="min-w-0 max-w-full"
        >
          <div className="max-w-full overflow-x-auto overscroll-x-contain pb-1 [scrollbar-width:thin]">
          <TabsList className="w-max max-w-none">
            <TabsTrigger value="all" className="shrink-0">All ({entries.length})</TabsTrigger>
            {sources.map((s) => (
              <TabsTrigger key={s.id} value={s.id} className="shrink-0">
                {s.name}
                <span className="ml-1 text-muted-foreground">
                  ({counts.get(s.id) ?? 0})
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
          </div>
        </Tabs>
        <div className="flex max-w-full items-center gap-1.5 overflow-x-auto pb-1 text-xs [scrollbar-width:thin]">
          <span className="text-muted-foreground">Show:</span>
          <SortChip active={saveFilter === 'all'} onClick={() => setSaveFilter('all')}>
            All saved
          </SortChip>
          <SortChip active={saveFilter === 'app'} onClick={() => setSaveFilter('app')}>
            Bunko saves
          </SortChip>
          <SortChip active={saveFilter === 'source'} onClick={() => setSaveFilter('source')}>
            Source favorites
          </SortChip>
        </div>
        <div className="flex max-w-full items-center gap-1.5 overflow-x-auto pb-1 text-xs [scrollbar-width:thin]">
          <span className="text-muted-foreground">Sort:</span>
          <SortChip active={sort === 'recent'} onClick={() => setSort('recent')}>
            Recently added
          </SortChip>
          <SortChip active={sort === 'title'} onClick={() => setSort('title')}>
            Title A–Z
          </SortChip>
        </div>
      </div>

      {loading && entries.length === 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[3/4] w-full" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <EmptyState />
      ) : sourceFilter === 'all' ? (
        visibleBySource.length > 0 ? (
          <div className="flex flex-col gap-5">
            {visibleBySource.map(({ source, cards }) => (
              <section key={source.id} className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2 border-b border-border/40 pb-1">
                  <h2 className="text-sm font-semibold">{source.name}</h2>
                  <span className="text-xs text-muted-foreground">{cards.length}</span>
                </div>
                {staleSourceSet.has(source.id) && <StaleSourceNotice sourceName={source.name} />}
                <div
                  aria-label={`${source.name} saved rail`}
                  className="flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 [scrollbar-width:thin]"
                >
                  {cards.map((card) => {
                    const entry = entryByCardKey.get(`${card.source}|${card.id}`);
                    return entry ? (
                      <div key={`${card.source}|${card.id}`} className="w-36 shrink-0 snap-start sm:w-40">
                        <LibraryCard card={card} entry={entry} />
                      </div>
                    ) : null;
                  })}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No saved comics match the current filters.</p>
        )
      ) : (
        <>
          {staleSourceSet.has(sourceFilter) && (
            <StaleSourceNotice sourceName={getSource(sourceFilter)?.name ?? sourceFilter} />
          )}
          <div
            role="grid"
            aria-label={`Saved ${getSource(sourceFilter)?.name ?? sourceFilter} favorites`}
            className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
          >
            {pagedVisible.flatMap((c) => {
              const entry = entryByCardKey.get(`${c.source}|${c.id}`);
              return entry ? [<LibraryCard key={`${c.source}|${c.id}`} card={c} entry={entry} />] : [];
            })}
          </div>
          {hasMoreToShow && loadedPages > 0 && (
            <div className="flex justify-center">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={loadMore}
                disabled={pageLoading}
                aria-label="Load more"
              >
                {pageLoading ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    Loading…
                  </>
                ) : (
                  `Load more (${visible.length - pagedVisible.length} hidden)`
                )}
              </Button>
            </div>
          )}
        </>
      )}

      {dedupGroups.length > 0 && (
        <section className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => setMatchesOpen((o) => !o)}
            className="group flex items-center justify-between rounded-md border border-border/40 bg-card px-3 py-2 text-left text-sm hover:bg-accent/30"
          >
            <span>
              Cross-source matches{' '}
              <span className="text-muted-foreground">({dedupGroups.length})</span>
            </span>
            {matchesOpen ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
          {matchesOpen && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {dedupGroups.map((g) => (
                <MatchGroup key={g.canonicalTitle} group={g} />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function StaleSourceNotice({ sourceName }: { sourceName: string }) {
  return (
    <p className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
      Reconnect {sourceName} to refresh source-site favorite state. Your Bunko saves remain available.
    </p>
  );
}

function LibraryCard({ card, entry }: { card: ComicCard; entry: FavoriteEntry }) {
  return (
    <div className="relative">
      <ComicCardComp
        comic={card}
        backState={{ fromPath: '/library', fromLabel: 'Saved' }}
      />
      <Badge
        variant="secondary"
        className="absolute left-2 top-2 px-1.5 py-0 text-[10px] uppercase"
        title={getSource(entry.sourceId)?.name ?? entry.sourceId}
      >
        {sourceBadge(entry.sourceId)}
      </Badge>
      <div className="absolute bottom-2 left-2 flex max-w-[calc(100%-1rem)] flex-wrap gap-1">
        {entry.appSaved && (
          <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
            Bunko
          </Badge>
        )}
        {entry.sourceSaved && (
          <Badge variant="outline" className="bg-background/90 px-1.5 py-0 text-[10px]">
            Source
          </Badge>
        )}
      </div>
    </div>
  );
}

function SortChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-2 py-0.5',
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border/60 text-muted-foreground hover:bg-accent/30',
      )}
    >
      {children}
    </button>
  );
}

function EmptyState() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>No saves yet</CardTitle>
        <CardDescription>
          Save anything to your Bunko library from a gallery page. Connected sources can also sync
          source-site favorites.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        {getAllSources().map((s) =>
          s.capabilities.login ? (
            <Link key={s.id} to={`/connect/${s.id}`}>
              <Button variant="outline" size="sm">
                Connect {s.name}
              </Button>
            </Link>
          ) : (
            <Link key={s.id} to={`/source/${s.id}`}>
              <Button variant="outline" size="sm">
                Browse {s.name}
              </Button>
            </Link>
          ),
        )}
      </CardContent>
    </Card>
  );
}

function ReconnectBanner({ err }: { err: SourceFavoritesError }) {
  const source = getSource(err.sourceId);
  const label = source?.name ?? err.sourceId;
  const verb =
    err.kind === 'auth' ? 'Reconnect' : err.kind === 'cloudflare' ? 'Re-verify' : 'Retry';
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-yellow-500/40 bg-yellow-500/10 px-3 py-2 text-sm">
      <div className="flex items-center gap-2">
        <AlertCircle className="h-4 w-4 text-yellow-600" />
        <span>
          <strong className="font-medium">{label}</strong>
          {': '}
          {err.message}
          {' Bunko saves are still available.'}
        </span>
      </div>
      {err.kind === 'auth' || err.kind === 'cloudflare' ? (
        <Link to={`/connect/${err.sourceId}`}>
          <Button size="sm" variant="outline">
            {verb} {label}
          </Button>
        </Link>
      ) : null}
    </div>
  );
}

function formatRelative(ts: number): string {
  const elapsed = Date.now() - ts;
  if (elapsed < 60_000) return 'just now';
  const m = Math.floor(elapsed / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(ts).toLocaleDateString();
}
